import { Check, Poll } from '@/components/ui/icons';
import { useEffect, useState } from 'react';
import type { MessageDTO, PollDTO } from '@clanfire/shared';
import { endPoll, votePoll } from '@/lib/messaging';
import { useChannelPermissions } from '@/lib/permissions';
import { formatUntil } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useUsers } from '@/stores/users';

/** Re-renders every half minute while a poll is open, so "ends in" and closing stay current. */
function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

/**
 * A poll in a message. Results show once you've voted, when it has closed, or
 * to its author. Picking an answer votes straight away; pick it again to undo.
 */
export function PollCard({ message, poll }: { message: MessageDTO; poll: PollDTO }) {
  const myId = useAuth((s) => s.user?.id ?? '');
  const users = useUsers((s) => s.users);
  const canManage = useChannelPermissions(message.channelId).can('MANAGE_MESSAGES');
  const [busy, setBusy] = useState(false);
  const endsAt = poll.endsAt ? new Date(poll.endsAt).getTime() : null;
  // The timer re-renders while it's open; an update (e.g. "End poll") re-renders now.
  const now = Math.max(useNow(endsAt !== null), Date.now());
  const closed = endsAt !== null && endsAt <= now;

  const mine = poll.options.filter((o) => o.voterIds.includes(myId)).map((o) => o.id);
  const voters = new Set(poll.options.flatMap((o) => o.voterIds));
  const isAuthor = message.author?.id === myId;
  const showResults = closed || mine.length > 0 || isAuthor;
  const top = Math.max(0, ...poll.options.map((o) => o.voterIds.length));

  const choose = async (optionId: string) => {
    if (closed || busy) return;
    const next = poll.allowMultiple
      ? mine.includes(optionId)
        ? mine.filter((id) => id !== optionId)
        : [...mine, optionId]
      : mine.includes(optionId)
        ? []
        : [optionId];
    setBusy(true);
    await votePoll(message, next);
    setBusy(false);
  };

  const names = (ids: string[]) => {
    const list = ids.map((id) => users[id]?.displayName ?? 'someone');
    return list.length > 4 ? `${list.slice(0, 4).join(', ')} and ${list.length - 4} more` : list.join(', ');
  };

  return (
    <section className="mt-1.5 w-full max-w-md rounded-2xl border border-line bg-sidebar p-3.5" aria-label={`Poll: ${poll.question}`}>
      <div className="mb-1 flex items-start gap-2">
        <Poll size={18} className="mt-0.5 shrink-0 text-accent" />
        <h3 className="font-display text-[17px] leading-snug text-fg">{poll.question}</h3>
      </div>
      <p className="mb-2.5 text-xs text-fg-muted">{closed ? 'Final results' : poll.allowMultiple ? 'Pick one or more answers' : 'Pick one answer'}</p>
      <ul className="flex flex-col gap-1.5">
        {poll.options.map((option) => {
          const count = option.voterIds.length;
          const share = voters.size ? Math.round((count / voters.size) * 100) : 0;
          const picked = mine.includes(option.id);
          const winning = closed && count > 0 && count === top;
          return (
            <li key={option.id}>
              <button
                type="button"
                disabled={closed || busy}
                onClick={() => void choose(option.id)}
                aria-pressed={picked}
                aria-label={`${option.text}${showResults ? `, ${count} ${count === 1 ? 'vote' : 'votes'} (${share}%)` : ''}`}
                title={showResults && count ? names(option.voterIds) : undefined}
                className={cn(
                  'relative flex w-full items-center gap-2.5 overflow-hidden rounded-xl border px-3 py-2 text-left text-sm transition-colors',
                  picked ? 'border-accent' : 'border-line',
                  !closed && 'hover:border-line-strong',
                  'disabled:cursor-default',
                )}
              >
                {showResults && (
                  <span
                    aria-hidden
                    className={cn('absolute inset-y-0 left-0 transition-[width] duration-300', picked || winning ? 'bg-accent-soft' : 'bg-hover')}
                    style={{ width: `${share}%` }}
                  />
                )}
                <span
                  className={cn(
                    'relative flex h-4.5 w-4.5 shrink-0 items-center justify-center border-2',
                    poll.allowMultiple ? 'rounded-md' : 'rounded-full',
                    picked ? 'border-accent bg-accent text-accent-fg' : 'border-line-strong',
                  )}
                >
                  {picked && <Check size={11} />}
                </span>
                <span className={cn('relative min-w-0 flex-1 break-words', winning && 'font-bold')}>{option.text}</span>
                {showResults && (
                  <span className="relative shrink-0 text-xs font-bold text-fg-soft tabular-nums">
                    {share}% <span className="font-normal text-fg-muted">({count})</span>
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
        <span>
          {voters.size} {voters.size === 1 ? 'vote' : 'votes'} · {closed ? 'Poll closed' : endsAt ? `Ends ${formatUntil(poll.endsAt!, now)}` : 'Open'}
        </span>
        {!closed && mine.length > 0 && (
          <button type="button" disabled={busy} onClick={() => void votePoll(message, [])} className="text-link hover:underline">
            Remove vote
          </button>
        )}
        {!closed && (isAuthor || canManage) && (
          <button type="button" onClick={() => void endPoll(message)} className="ml-auto text-link hover:underline">
            End poll
          </button>
        )}
      </div>
    </section>
  );
}
